/**
 * Mid-session JWT refresh runner — the real planner armed on the
 * refresh ladder by `initialMint` (Slice 2 of the auth wiring; T306 /
 * T3016).
 *
 * `initial-mint.ts` arms `scheduleRefresh` with an injected
 * `runRefresh: () => Promise<RefreshOutcome>`. Absent an injected
 * runner it arms a documented no-op (bootstrap-only / unit-test path).
 * This module builds the production runner: it wraps
 * `connectivity/jwt/jwt-refresh.ts`'s `refreshJwtMidSession` (the pure
 * planner: pre-expiry → mint → jwtStore.update → nc.reconnect →
 * snapshot lastSeq → reconcile → apply) and adapts its
 * `JwtRefreshResult` to the ladder's `RefreshOutcome`, carrying the
 * freshly-minted expiry so the ladder re-arms for the next cycle.
 *
 * Why a dedicated module (not inlined in SDKProvider): the refresh
 * transport wiring is structural SDK logic — it must be unit-testable
 * in isolation, and the provider stays composition-only.
 *
 * `/v1/reconnect-snapshot` reality: the planner's `reconcile` step is a
 * spec'd backend contract that is NOT implemented in the live API yet
 * (sandbox-mocked in the harness slice). Until it answers, this runner
 * does NOT fabricate a server snapshot — it preserves the local
 * lastSeq floor and recovers missed events by refetching realtime
 * queries in `applyReconcile`. This is the exact fallback the
 * connectivity orchestrator already uses for the not-yet-shipped bulk
 * endpoint (`runBulkSnapshotWith503Fallback` per-query refetch), not a
 * stub.
 *
 * Lives under `src/nats/auth/` — an allowed `no-direct-fetch` path
 * (auth fetches the NATS JWT before the dispatcher is up); `fetchImpl`
 * is still injectable for tests.
 */

import type { NatsConnection } from '@nats-io/nats-core';

import type {
  JwtRefreshDeps,
  JwtRefreshResult,
  ReconnectSnapshotResponse,
} from '../../connectivity/jwt/jwt-refresh.js';
import { refreshJwtMidSession as realRefreshJwtMidSession } from '../../connectivity/jwt/jwt-refresh.js';
import { NATS_CLIENT_GLOBAL_KEY } from '../connect/globals.js';
import { _peekLastSeqs } from '../last-seq-store.js';
import { type MintClientConfig, mintCredentials } from './auth.js';
import { getJwtStore } from './jwt-store.js';
import { REFRESH_LEAD_TIME_MS, type RefreshOutcome } from './refresh-ladder.js';

/** Canonical reconnect-reconcile endpoint (relative — the SDK fetch
 *  shim / sandbox proxy routes it; not yet implemented server-side). */
export const RECONNECT_SNAPSHOT_PATH = '/v1/reconnect-snapshot';

/**
 * Busy-loop floor. The ladder schedules the next refresh at
 * `expiresAt − REFRESH_LEAD_TIME_MS − now`. If a mint returns a TTL
 * SHORTER than the 10-min lead (clock skew, a misconfigured endpoint,
 * or — in the sandbox — the 30 s mock mint), that delay is ≤ 0, the
 * ladder fires immediately, re-mints, re-arms, fires immediately… an
 * infinite tight loop that DoS-hammers the mint + reconcile endpoints
 * and thrashes `nc.reconnect()`. We clamp the re-armed expiry so the
 * effective delay is never below this floor. Production is unaffected
 * (real anon TTL ≈ 2 h, user ≈ 4 h ≫ lead → clamp never triggers); it
 * is a pure safety net + the fix for short-TTL test mints.
 */
export const MIN_REARM_INTERVAL_MS = 60_000;

export interface RefreshRunnerDeps {
  /** Same mint config the bootstrap `initialMint` uses. */
  readonly mintConfig: MintClientConfig;
  /**
   * Resolve the bearer for the CURRENT identity (`null` = anon). The
   * refresh re-mints under whatever identity is live at fire time.
   */
  readonly resolveBearer: () => Promise<string | null>;
  /**
   * Refetch realtime queries — the reconcile-recovery path when
   * `/v1/reconnect-snapshot` is absent. Wired by SDKProvider against
   * the QueryClient (`refetchQueries({ predicate: meta.realtime })`).
   */
  readonly refetchRealtime: () => Promise<void> | void;
  /** Injected clock (defaults to `Date.now`). */
  readonly now?: () => number;
  /** Injected fetch (defaults to `globalThis.fetch`); for tests. */
  readonly fetchImpl?: typeof fetch;
  /** Injectable planner; defaults to the real one. For tests. */
  readonly refreshJwtMidSession?: (deps: JwtRefreshDeps) => Promise<JwtRefreshResult>;
  /** Live NC accessor; defaults to the canonical globalThis pin. */
  readonly getLiveNc?: () => NatsConnection | null;
  /** Fired on every failed refresh attempt (→ `reportNatsFailure`). */
  readonly onFailure?: (attempt: number, error: unknown, nextRetryMs: number | null) => void;
}

/**
 * Build the `runRefresh` passed into `initialMint`. The returned
 * function is invoked by the refresh ladder at `exp − lead`; it
 * resolves to a `RefreshOutcome` so the ladder re-arms on success.
 */
export function createRefreshRunner(deps: RefreshRunnerDeps): () => Promise<RefreshOutcome> {
  const now = deps.now ?? ((): number => Date.now());
  const planner = deps.refreshJwtMidSession ?? realRefreshJwtMidSession;
  const getLiveNc =
    deps.getLiveNc ??
    ((): NatsConnection | null =>
      (globalThis as { [NATS_CLIENT_GLOBAL_KEY]?: NatsConnection })[NATS_CLIENT_GLOBAL_KEY] ??
      null);
  const doFetch =
    deps.fetchImpl ??
    (typeof globalThis.fetch === 'function' ? globalThis.fetch.bind(globalThis) : undefined);

  return async function runRefresh(): Promise<RefreshOutcome> {
    // Captured by the `mint` closure so the ladder can re-arm against
    // the freshly-minted expiry (`refreshJwtMidSession` does not return
    // it).
    let nextExpiresAtMs = 0;

    const result = await planner({
      store: getJwtStore(),
      now,
      ...(deps.onFailure !== undefined ? { onFailure: deps.onFailure } : {}),
      mint: async () => {
        const bearer = await deps.resolveBearer();
        const minted = await mintCredentials(deps.mintConfig, { bearerToken: bearer });
        nextExpiresAtMs = minted.expiresAt * 1_000; // epoch s → epoch ms
        return { jwt: minted.jwt, expiresAt: nextExpiresAtMs };
      },
      reconnect: async () => {
        const nc = getLiveNc();
        if (nc !== null) await nc.reconnect();
      },
      snapshotLastSeq: () => {
        const out: Record<string, number> = {};
        for (const [subject, entry] of _peekLastSeqs()) out[subject] = entry.seq;
        return out;
      },
      reconcile: async (lastSeqBySubject) => {
        if (doFetch === undefined) return { data: {}, lastSeqBySubject };
        try {
          const res = await doFetch(RECONNECT_SNAPSHOT_PATH, {
            method: 'POST',
            headers: { 'content-type': 'application/json', accept: 'application/json' },
            body: JSON.stringify({ lastSeqBySubject }),
          });
          if (res.ok) {
            const body = (await res.json()) as Partial<ReconnectSnapshotResponse>;
            return {
              data: body.data ?? {},
              lastSeqBySubject: body.lastSeqBySubject ?? lastSeqBySubject,
            };
          }
        } catch {
          // Endpoint absent / network failure → preserve the local
          // lastSeq floor; applyReconcile recovers via realtime refetch.
        }
        return { data: {}, lastSeqBySubject };
      },
      applyReconcile: () => {
        void deps.refetchRealtime();
      },
    });

    if (!result.success) return { success: false };

    // Anti-busy-loop clamp: guarantee the ladder's next delay
    // (`nextExpiresAt − REFRESH_LEAD_TIME_MS − now`) is ≥
    // MIN_REARM_INTERVAL_MS. No-op when the real minted TTL exceeds the
    // lead window (production); the floor only engages for pathological
    // short-TTL mints (sandbox 30 s mock / clock skew) where it converts
    // an infinite immediate-refire into a sane fixed cadence.
    const earliest = now() + REFRESH_LEAD_TIME_MS + MIN_REARM_INTERVAL_MS;
    return { success: true, nextExpiresAt: Math.max(nextExpiresAtMs, earliest) };
  };
}
